import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPatch, apiPost, apiPut } from '../../../api/client';
import { PageHeader } from '../../../components/common/PageHeader';
import { SelectField } from '../../../components/fields/SelectField';
import { PermissionGate } from '../../../components/auth/PermissionGate';
import { useAuthStore } from '../../../store/auth.store';
import { ReasonDialog } from './ReasonDialog';
import { EvidencePicker, EvidenceFilesDialog } from '../evidence';
import type { EvidenceItem } from '../evidence/evidenceLogic';
import {
  APPROVAL_REASON_LABEL, EXPENSE_STATUS_LABEL, ExpenseAction, ExpenseFormValues, PAYMENT_MODES, evidenceRequired, expenseActions, expenseFormError,
  isMoney, limitPreview, statusColor, warningText,
} from './expenseLogic';

// Branch Cash - expenses (P3): enter an expense, see the day's spending against the branch limit, approve / reject / pay / cancel /
// reverse, and (with cash:config_limits) change the limit and manage categories. Buttons follow the user's permissions and the
// expense's state; the server decides everything (limits, evidence, separation of duties, branch grants).

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const REFRESH = ['bc-expenses', 'bc-exp-pending', 'bc-exp-usage', 'bc-summary', 'bc-moves', 'bc-blockers'];

function ExpenseForm({ branchId, categories, usage, initial, submitLabel, busy, onSubmit }: {
  branchId: string; categories: any[]; usage: any; initial?: Partial<ExpenseFormValues>; submitLabel: string; busy: boolean; onSubmit: (v: ExpenseFormValues & { evidenceFileIds: string[] }) => void;
}) {
  const canAttach = useAuthStore((s) => s.hasPermission)('cash:evidence_upload');
  const [files, setFiles] = useState<EvidenceItem[]>([]);
  const [f, setF] = useState<ExpenseFormValues & { payee: string; referenceNo: string; notes: string }>({
    categoryId: '', description: '', amount: '', paymentMode: 'CASH', evidenceRef: '', evidenceExceptionReason: '', payee: '', referenceNo: '', notes: '', ...initial,
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }));
  const cat = categories.find((c) => c.id === f.categoryId);
  const needEvidence = evidenceRequired(f.amount, cat?.evidenceThreshold);
  const error = expenseFormError(f, needEvidence, files.length);
  const preview = limitPreview(usage, f.amount);
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!error && !busy) onSubmit({ ...f, evidenceFileIds: files.map((x) => x.id) }); }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
        <SelectField label="Category" value={f.categoryId} onChange={set('categoryId') as any} emptyLabel="Select..."
          options={categories.filter((c) => c.isActive || c.id === initial?.categoryId).map((c) => ({ value: c.id, label: c.name }))} />
        <TextField label="Description" value={f.description} onChange={set('description')} InputLabelProps={{ shrink: true }} />
        <TextField label="Amount (₹)" value={f.amount} onChange={set('amount')} InputLabelProps={{ shrink: true }} error={f.amount !== '' && !isMoney(f.amount)} />
        <SelectField label="Payment mode" value={f.paymentMode} onChange={set('paymentMode') as any} options={PAYMENT_MODES} />
        <TextField label="Paid to (payee)" value={f.payee} onChange={set('payee')} InputLabelProps={{ shrink: true }} />
        <TextField label="Reference no." value={f.referenceNo} onChange={set('referenceNo')} InputLabelProps={{ shrink: true }} />
        <TextField label={needEvidence ? 'Evidence reference (required)' : 'Evidence reference'} value={f.evidenceRef} onChange={set('evidenceRef')} InputLabelProps={{ shrink: true }}
          helperText="Bill / voucher number (or attach a file below)" />
        {needEvidence && !f.evidenceRef.trim() && files.length === 0 && (
          <TextField label="No evidence? Say why" value={f.evidenceExceptionReason} onChange={set('evidenceExceptionReason')} InputLabelProps={{ shrink: true }}
            helperText="An exception sends the expense for approval" />
        )}
      </Box>
      {canAttach && <Box sx={{ mt: 1.5 }}><EvidencePicker branchId={branchId} ownerType="EXPENSE" items={files} onChange={setFiles} disabled={busy} /></Box>}
      {preview.needsApproval && <Alert severity="warning" sx={{ mt: 1 }}>This takes today's expenses to {rupees(preview.after)}, over the limit - it will need approval before it can be paid.</Alert>}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Button type="submit" variant="contained" disabled={!!error || busy}>{submitLabel}</Button>
        {error && f.amount !== '' && <Typography variant="caption" color="error">{error}</Typography>}
      </Stack>
    </form>
  );
}

function ConfigPanel({ branchId, categories }: { branchId: string; categories: any[] }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [limit, setLimit] = useState(''); const [effective, setEffective] = useState('NEXT_DAY'); const [reason, setReason] = useState('');
  const [catName, setCatName] = useState(''); const [catThreshold, setCatThreshold] = useState('');
  const limitsQ = useQuery({ queryKey: ['bc-exp-limits', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/expenses/limits', { branchId })) as any });
  const done = (msg: string) => { REFRESH.concat(['bc-exp-limits', 'bc-exp-cats']).forEach((k) => qc.invalidateQueries({ queryKey: [k] })); enqueueSnackbar(msg, { variant: 'success' }); };
  const fail = (e: any) => enqueueSnackbar(errMsg(e, 'Could not save'), { variant: 'error' });
  const change = useMutation({
    mutationFn: () => apiPost('/branch-cash/expenses/limits', { branchId, dailyLimit: limit.trim(), effective, reason: reason.trim() }),
    onSuccess: () => { setLimit(''); setReason(''); done('Limit saved'); }, onError: fail,
  });
  const addCat = useMutation({
    mutationFn: () => apiPost('/branch-cash/expenses/categories', { code: catName.trim(), name: catName.trim(), evidenceThreshold: catThreshold.trim() === '' ? null : catThreshold.trim() }),
    onSuccess: () => { setCatName(''); setCatThreshold(''); done('Category added'); }, onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (c: any) => apiPatch(`/branch-cash/expenses/categories/${c.id}`, { isActive: !c.isActive }), onSuccess: () => done('Category updated'), onError: fail,
  });
  const cur = limitsQ.data?.current;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Daily expense limit &amp; categories</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>Limit in force: <b>{cur ? rupees(cur.dailyLimit) : 'none set'}</b>{cur ? ` (from ${cur.effectiveFrom})` : ''}</Typography>
      <form onSubmit={(e) => { e.preventDefault(); if (isMoney(limit) && reason.trim() && !change.isPending) change.mutate(); }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
          <TextField label="New daily limit (₹)" value={limit} onChange={(e) => setLimit(e.target.value)} InputLabelProps={{ shrink: true }} />
          <SelectField label="Applies" value={effective} onChange={(e) => setEffective(e.target.value)}
            options={[{ value: 'NOW', label: 'Immediately' }, { value: 'NEXT_DAY', label: 'From the next business day' }]} />
          <TextField label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <Button type="submit" variant="contained" size="small" sx={{ mt: 1 }} disabled={!(isMoney(limit) && reason.trim()) || change.isPending}>Save limit</Button>
      </form>
      {!!limitsQ.data?.history?.length && (
        <Table size="small" sx={{ mt: 1.5 }}>
          <TableHead><TableRow><TableCell>From</TableCell><TableCell align="right">Limit</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
          <TableBody>{limitsQ.data.history.slice(0, 6).map((h: any) => (
            <TableRow key={h.id}><TableCell>{h.effectiveFrom}{h.inForce ? ' (in force)' : ''}</TableCell><TableCell align="right">{rupees(h.dailyLimit)}</TableCell><TableCell>{h.reason ?? '-'}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      )}
      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Categories</Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {categories.map((c) => <Chip key={c.id} size="small" label={c.name} color={c.isActive ? 'primary' : 'default'} variant={c.isActive ? 'filled' : 'outlined'} onClick={() => toggle.mutate(c)} title="Click to activate / deactivate" />)}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" label="New category" value={catName} onChange={(e) => setCatName(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Evidence from (₹, optional)" value={catThreshold} onChange={(e) => setCatThreshold(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" variant="outlined" disabled={!catName.trim() || (catThreshold !== '' && !isMoney(catThreshold)) || addCat.isPending} onClick={() => addCat.mutate()}>Add</Button>
      </Stack>
    </Paper>
  );
}

export function ExpensesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user?.id);
  const has = useAuthStore((s) => s.hasPermission);
  const settingsQ = useQuery({ queryKey: ['bc-settings'], queryFn: async () => unwrap(await apiGet('/branch-cash/settings')) as any[] });
  const branches = (settingsQ.data ?? []) as any[];
  const [branchId, setBranchId] = useState('');
  useEffect(() => { if (!branchId && branches.length) setBranchId(branches[0].id); }, [branches, branchId]);
  const [status, setStatus] = useState('');
  const [dialog, setDialog] = useState<{ id: string; action: ExpenseAction } | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [filesFor, setFilesFor] = useState<{ id: string; number: string } | null>(null);

  const catsQ = useQuery({ queryKey: ['bc-exp-cats'], queryFn: async () => unwrap(await apiGet('/branch-cash/expenses/categories')) as any[] });
  const usageQ = useQuery({ queryKey: ['bc-exp-usage', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/expenses/usage', { branchId })) as any });
  const listQ = useQuery({ queryKey: ['bc-expenses', branchId, status], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/expenses', { branchId, status: status || undefined, limit: 100 })) as any });
  const pendingQ = useQuery({ queryKey: ['bc-exp-pending', branchId], enabled: !!branchId && has('cash:expense_approve'), queryFn: async () => unwrap(await apiGet('/branch-cash/expenses/pending-approvals', { branchId })) as any[] });
  const categories = catsQ.data ?? []; const usage = usageQ.data; const rows: any[] = listQ.data?.rows ?? []; const pending = pendingQ.data ?? [];
  const refresh = () => REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const fail = (e: any) => enqueueSnackbar(errMsg(e, 'The action was refused'), { variant: 'error' });

  const create = useMutation({
    mutationFn: async (v: any) => unwrap(await apiPost('/branch-cash/expenses', { branchId, ...v, evidenceExceptionReason: v.evidenceRef?.trim() ? undefined : v.evidenceExceptionReason || undefined, evidenceRef: v.evidenceRef || undefined })) as any,
    onSuccess: (r: any) => { refresh(); setFormKey((k) => k + 1); enqueueSnackbar(r.expense.status === 'PENDING_APPROVAL' ? `${r.expense.expenseNumber} saved - awaiting approval` : `${r.expense.expenseNumber} saved - ready to pay`, { variant: 'success' }); },
    onError: fail,
  });
  const resubmit = useMutation({
    mutationFn: async (v: any) => unwrap(await apiPut(`/branch-cash/expenses/${editing.id}`, { ...v, evidenceExceptionReason: v.evidenceRef?.trim() ? undefined : v.evidenceExceptionReason || undefined, evidenceRef: v.evidenceRef || undefined })) as any,
    onSuccess: () => { refresh(); setEditing(null); enqueueSnackbar('Expense resubmitted', { variant: 'success' }); }, onError: fail,
  });
  const act = useMutation({
    mutationFn: async (p: { id: string; action: string; reason?: string }) =>
      apiPost(`/branch-cash/expenses/${p.id}/${p.action}`, p.action === 'reject' || p.action === 'approve' ? { note: p.reason } : { reason: p.reason }),
    onSuccess: (_d, p) => { refresh(); setDialog(null); enqueueSnackbar(`Expense ${p.action === 'pay' ? 'paid' : p.action + (p.action.endsWith('e') ? 'd' : 'ed')}`, { variant: 'success' }); },
    onError: fail,
  });

  const ActionButtons = ({ e }: { e: any }) => (
    <Stack direction="row" spacing={0.5}>
      {expenseActions(e, me, has).map((a) => (
        <Button key={a} size="small" variant={a === 'approve' || a === 'pay' ? 'contained' : 'outlined'} color={a === 'reject' || a === 'reverse' || a === 'cancel' ? 'error' : 'primary'} disabled={act.isPending}
          onClick={() => (a === 'approve' || a === 'pay' ? act.mutate({ id: e.id, action: a }) : a === 'resubmit' ? setEditing(e) : setDialog({ id: e.id, action: a }))}>
          {a === 'resubmit' ? 'Edit & resubmit' : a[0].toUpperCase() + a.slice(1)}
        </Button>
      ))}
    </Stack>
  );
  const Reasons = ({ e }: { e: any }) => (
    <>{(e.approvalReasons ?? []).map((r: string) => <Chip key={r} size="small" sx={{ ml: 0.5 }} variant="outlined" label={APPROVAL_REASON_LABEL[r] ?? r} />)}</>
  );
  const warn = warningText(usage);
  const noDay = usage && !usage.dayId;

  return (
    <Box>
      <PageHeader title="Branch expenses" subtitle="Enter, approve and pay day-to-day expenses against the branch's daily limit" />
      {settingsQ.isError && <Alert severity="error" sx={{ mb: 2 }}>{errMsg(settingsQ.error, 'Could not load branches')}</Alert>}
      <Stack spacing={2}>
        <Box sx={{ maxWidth: 320 }}>
          <SelectField label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} options={branches.map((b) => ({ value: b.id, label: `${b.branchCode} - ${b.name}` }))} />
        </Box>

        {noDay && <Alert severity="info">No business day is open for this branch - open it on the Branch Cash screen before entering expenses.</Alert>}
        {usage?.dayId && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2">Business day <b>{usage.businessDate}</b></Typography>
              <Typography variant="body2">Daily limit <b>{usage.limit === null ? 'none' : rupees(usage.limit)}</b></Typography>
              <Typography variant="body2">Counted today <b>{rupees(usage.used)}</b></Typography>
              {usage.remaining !== null && <Typography variant="body2">Remaining <b>{rupees(usage.remaining)}</b></Typography>}
              {usage.exceeded && <Chip size="small" color="warning" label="Over limit - approval needed" />}
            </Stack>
            {warn && <Alert severity={usage.exceeded || usage.warningLevel === 3 ? 'warning' : 'info'} sx={{ mt: 1 }}>{warn}</Alert>}
          </Paper>
        )}

        <PermissionGate permission="cash:expense_create">
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>New expense</Typography>
            <ExpenseForm key={formKey} branchId={branchId} categories={categories} usage={usage} submitLabel="Save expense" busy={create.isPending || !!noDay} onSubmit={(v) => create.mutate(v)} />
          </Paper>
        </PermissionGate>

        <PermissionGate permission="cash:expense_approve">
          {pending.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Awaiting your decision ({pending.length})</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>No.</TableCell><TableCell>Category</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell><TableCell>Why approval</TableCell><TableCell>Decision</TableCell></TableRow></TableHead>
                  <TableBody>{pending.map((e: any) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.expenseNumber}</TableCell><TableCell>{e.categoryName}</TableCell><TableCell>{e.description}{e.evidenceStatus === 'EXCEPTION' ? ` (no evidence: ${e.evidenceExceptionReason})` : ''}</TableCell>
                      <TableCell align="right">{rupees(e.amount)}</TableCell><TableCell><Reasons e={e} /></TableCell>
                      <TableCell>{e.canDecide ? <ActionButtons e={e} /> : <Typography variant="caption">You submitted this - another person must decide</Typography>}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </Box>
            </Paper>
          )}
        </PermissionGate>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Expense register</Typography>
            <Box sx={{ width: 220 }}>
              <SelectField size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} emptyLabel="All"
                options={Object.entries(EXPENSE_STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
            </Box>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>No.</TableCell><TableCell>Date</TableCell><TableCell>Category</TableCell><TableCell>Description</TableCell><TableCell>Mode</TableCell>
                <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{e.expenseNumber}</TableCell><TableCell>{new Date(e.expenseDate).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>{e.categoryName}</TableCell>
                    <TableCell>{e.description}{e.status === 'REJECTED' && e.decisionNote ? ` - rejected: ${e.decisionNote}` : ''}
                      {e.evidenceFileCount > 0 && <Button size="small" sx={{ ml: 1 }} onClick={() => setFilesFor({ id: e.id, number: e.expenseNumber })}>Files ({e.evidenceFileCount})</Button>}
                    </TableCell>
                    <TableCell>{e.paymentMode}</TableCell><TableCell align="right">{rupees(e.amount)}</TableCell>
                    <TableCell><Chip size="small" color={statusColor(e.status)} label={EXPENSE_STATUS_LABEL[e.status] ?? e.status} /><Reasons e={e} /></TableCell>
                    <TableCell><ActionButtons e={e} /></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No expenses.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <PermissionGate permission="cash:config_limits">{branchId && <ConfigPanel branchId={branchId} categories={categories} />}</PermissionGate>
      </Stack>

      <EvidenceFilesDialog open={!!filesFor} ownerType="EXPENSE" ownerId={filesFor?.id ?? null} title={`Files attached to ${filesFor?.number ?? ''}`} onClose={() => setFilesFor(null)} />
      <ReasonDialog open={!!dialog} busy={act.isPending} onClose={() => setDialog(null)}
        title={dialog?.action === 'reject' ? 'Reject expense' : dialog?.action === 'cancel' ? 'Cancel expense' : 'Reverse paid expense'}
        message={dialog?.action === 'reverse' ? 'A cash expense puts the money back into the till. This is recorded and cannot be undone.' : undefined}
        confirmLabel={dialog?.action === 'reject' ? 'Reject' : dialog?.action === 'cancel' ? 'Cancel expense' : 'Reverse'}
        onConfirm={(reason) => dialog && act.mutate({ id: dialog.id, action: dialog.action, reason })} />

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit and resubmit {editing?.expenseNumber}</DialogTitle>
        <DialogContent>
          {editing?.decisionNote && <Alert severity="info" sx={{ mb: 1.5 }}>Rejected: {editing.decisionNote}</Alert>}
          {editing && <ExpenseForm branchId={editing.branchId} categories={categories} usage={usage} busy={resubmit.isPending} submitLabel="Resubmit"
            initial={{ categoryId: editing.categoryId, description: editing.description, amount: String(editing.amount), paymentMode: editing.paymentMode, evidenceRef: editing.evidenceRef ?? '', evidenceExceptionReason: editing.evidenceExceptionReason ?? '' }}
            onSubmit={(v) => resubmit.mutate(v)} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setEditing(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
